import { QueryClient } from "@tanstack/react-query";
import { createApiClient, type Transport } from "./api/client";

/** The mock backend is its own chunk, fetched only in mock mode, so a production build that
 * talks to the real API never downloads it. */
function lazyMockTransport(opts: { mode: Exclude<DataMode, "http">; reset?: boolean }): Transport {
  let ready: Promise<Transport> | null = null;
  return async (req) => {
    ready ??= import("./mock/transport").then((m) => m.createMockTransport(opts));
    return (await ready)(req);
  };
}
import type { ApiValue } from "./api/context";
import { endpoints } from "./api/endpoints";
import { isApiError } from "./api/errors";
import { isMockMode, type DataMode } from "./api/mode";
import { sessionStore } from "./auth/session";

/** Builds the app's long-lived services once: the query cache, the API client (HTTP or the
 * mock backend) and its 401 handling. Tests call this with their own transport. */
export function createServices({ mode, reset, transport }: { mode: DataMode; reset?: boolean; transport?: Transport }) {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // Never retry a server's "no" (4xx); retry network/5xx twice.
        retry: (count, err) => !(isApiError(err) && err.status < 500) && count < 2,
      },
      mutations: { retry: false },
    },
  });
  const t = transport ?? (isMockMode(mode) ? lazyMockTransport({ mode: mode as Exclude<DataMode, "http">, reset }) : undefined);
  const client = createApiClient({
    transport: t,
    getToken: sessionStore.token,
    // §16.5: any 401 clears the session; RequireAuth then sends the user to /login.
    onUnauthorized: () => {
      sessionStore.clearActive();
      queryClient.clear();
    },
  });
  const api: ApiValue = { client, api: endpoints(client), mode };
  return { queryClient, api };
}
